import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Sparkles } from "lucide-react";
import { site } from "../../data/siteConfig";

const mask = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.25 + i * 0.14, ease: [0.33, 1, 0.68, 1] },
  }),
};

const MaskedLine = ({ index, children, className = "" }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className={`block ${className}`}
      variants={mask}
      custom={index}
      initial="hidden"
      animate="show"
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <section
      ref={ref}
      className="relative flex min-h-[94svh] items-end overflow-hidden"
      data-testid="home-hero"
    >
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: 1.18 }}>
        <img
          src="/assets/gallery/hero.jpg"
          alt="Woman with long box braids against a vivid backdrop"
          className="h-full w-full object-cover opacity-55"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/45 to-ink/30" />
      <div className="absolute inset-0 bg-gradient-to-r from-ink/70 via-transparent to-transparent" />

      <motion.img
        src={site.assets.logoMark}
        alt=""
        aria-hidden="true"
        className="absolute right-6 top-24 hidden h-28 w-28 animate-spin-slow rounded-full opacity-80 ring-1 ring-hotpink/40 md:block"
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 0.85, scale: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
      />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-24 pt-36 md:pb-32">
        <motion.p
          className="mb-6 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.4em] text-blush"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
        >
          <Sparkles className="h-4 w-4 animate-sparkle text-hotpink" />
          Braids • Protective Styles • Hampton, VA
        </motion.p>

        <h1 className="font-serif text-[13.5vw] font-bold leading-[0.95] tracking-tight sm:text-7xl lg:text-8xl">
          <MaskedLine index={0}>YAYA</MaskedLine>
          <MaskedLine index={1} className="italic text-hotpink text-glow-pink">
            JUST DID
          </MaskedLine>
          <MaskedLine index={2}>THAT</MaskedLine>
        </h1>

        <motion.p
          className="mt-6 font-script text-3xl text-blush md:text-4xl"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.7 }}
        >
          Beautiful braids. Clean parts. Confident looks.
        </motion.p>

        <motion.div
          className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.35, duration: 0.7 }}
        >
          <Link
            to="/book"
            data-testid="hero-book-appointment"
            className="rounded-full bg-hotpink px-9 py-4 text-center text-xs font-bold uppercase tracking-[0.25em] text-white shadow-[0_10px_40px_-10px_rgba(255,20,147,0.6)] transition-colors duration-300 hover:bg-magenta"
          >
            Book Your Appointment
          </Link>
          <Link
            to="/gallery"
            data-testid="hero-view-styles"
            className="rounded-full border border-white/25 px-9 py-4 text-center text-xs font-bold uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:border-hotpink hover:text-hotpink"
          >
            View Styles
          </Link>
        </motion.div>
      </div>

      <motion.div
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 md:block"
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
        aria-hidden="true"
      >
        <ArrowDown className="h-5 w-5 text-white/40" />
      </motion.div>
    </section>
  );
};

